import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const conditions = [{
  symptom: "Back, neck and nerve pain",
  desc: "Disc disorders, sciatica, radiculopathy, spinal stenosis and nerve compression causing burning, shooting or radiating pain."
}, {
  symptom: "Joint pain",
  desc: "Hip, knee, shoulder or other joint pain caused by arthritis, inflammation or degeneration."
}, {
  symptom: "Muscle and tendon pain",
  desc: "Myalgia, fibromyalgia, tendinitis, bursitis and epicondylitis."
}];
const procedures = [{
  title: "Epidural Steroid Injection",
  what: "A steroid mixture is injected into the epidural space, the gap between the spinal ligament and the dural sac. The medication works locally, reducing inflammation and pain around the spinal nerve roots and allowing the patient to recover faster.",
  how: "The procedure is carried out using a mobile C-arm X-ray unit to ensure precise delivery of the medication. Epidural injections can be applied to both the lumbar and cervical regions of the spine, depending on the location of the pain."
}, {
  title: "Invasive Procedure (TTB) / Trigger Point Treatment",
  what: "A trigger point forms when a specific area of muscle contracts and cannot relax, causing localised pain and disrupting circulation in the surrounding tissue.",
  how: "A mixture of anaesthetic and anti-inflammatory medication is injected directly into the painful muscle area. The procedure relaxes the contracted muscle, reduces inflammation and provides significant pain relief. Commonly used for neck, shoulder, back and lumbar pain related to poor posture, physical strain or injury."
}, {
  title: "Cryodenervation",
  what: "A minimally invasive procedure that uses extreme cold to interrupt the nerve signals responsible for transmitting chronic pain.",
  how: "The targeted nerve is precisely cooled to disrupt its ability to send pain signals. The effect is long-lasting and the procedure is performed without surgery or general anaesthesia."
}, {
  title: "PRP (Platelet-Rich Plasma) Therapy",
  what: "A treatment that uses the patient's own blood to stimulate natural tissue repair. A sample is processed to concentrate growth factors, which are then injected into the affected area.",
  how: "The concentrated plasma is injected into the damaged tissue, triggering the body's own healing response. Used for joint degeneration, tendon damage and chronic inflammation."
}, {
  title: "Prolotherapy and Joint Injections",
  what: "A treatment in which a solution is injected directly into damaged joints or connective tissue to stimulate the body's natural repair mechanisms.",
  how: "The injection triggers a controlled healing response in the treated area, strengthening weakened ligaments and reducing chronic joint pain. Used for joint instability, tendon conditions and persistent joint pain."
}];
export default function FiveWeekCourseDetails() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  return <section data-section-id="115669" id="pain-management-overview" className="bg-background text-foreground py-12 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">

        <div className="mb-12 md:mb-24">
          <div className="text-center mb-12">
            <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-3">Find out if we can help you</h2>
            <p className="text-base text-muted-foreground font-default max-w-2xl mx-auto">
              If you are living with any of the following, our specialists can assess your condition and recommend the most appropriate treatment, without surgery or a GP referral.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {conditions.map((item, i) => <div key={i} className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm p-6 flex flex-col gap-3">
                <h3 className="font-default font-bold text-primary text-lg leading-snug">{item.symptom}</h3>
                <p className="font-default text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>)}
          </div>
          <div className="mt-8 p-6 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
            <p className="font-default text-[#264256] text-sm text-center">
              Most procedures for the conditions above are eligible for CBD reimbursement, typically between €389 and €2,125 per procedure. Contact us for accurate information about your specific case.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-stretch relative mb-12 md:mb-24 gap-6 md:gap-12">
          <div className="w-full lg:w-[55%] relative z-0">
            <div className="relative w-full h-full min-h-[220px] md:min-h-[400px]">
              <img src="https://charming-joey.10web.cloud/wp-content/uploads/2026/06/DSC_4562-1-scaled.jpg" alt="Pain management procedure at Clinic Agatas" className="w-full h-full object-cover rounded-lg shadow-xl" />
              <div className="absolute inset-0 bg-primary/5 mix-blend-overlay rounded-lg pointer-events-none"></div>
            </div>
          </div>
          <div className="w-full lg:w-[45%] relative z-10">
            <Card className="bg-white border-[#f1f3f5] shadow-2xl p-8 md:p-10 rounded-lg h-full">
              <CardContent className="p-0 flex flex-col gap-6 h-full">
                <div className="space-y-3">
                  <h2 className="font-default text-3xl md:text-4xl font-bold text-primary leading-tight">Our Treatments</h2>
                  <p className="text-base text-muted-foreground font-default leading-relaxed">
                    A multidisciplinary team of pain specialists, neurologists, physiotherapists and psychologists. Every treatment plan is built around your specific condition, not a standard protocol.
                  </p>
                </div>
                <ul className="space-y-3">
                  {["Epidural steroid injections", "Invasive procedures under ultrasound or X-ray control", "Cryodenervation", "PRP (platelet-rich plasma) therapy", "Prolotherapy and joint injections"].map((item, i) => <li key={i} className="flex items-center gap-3 p-3 border border-[#f1f3f5] rounded-lg bg-white">
                      <div className="bg-accent/50 rounded-full p-1 shrink-0">
                        <Check className="w-4 h-4 text-chart-2" strokeWidth={3} />
                      </div>
                      <span className="text-primary font-default text-base leading-snug">{item}</span>
                    </li>)}
                </ul>
                <p className="text-sm text-muted-foreground font-default">Online consultation available before you travel.</p>
                <div className="pt-6 border-t border-border mt-auto">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="text-primary text-4xl font-default font-bold tracking-tight">€150</div>
                      <div className="text-sm text-muted-foreground mt-1 font-default">Initial consultation</div>
                    </div>
                    <Button size="lg" asChild className="w-full sm:w-auto text-base h-12 px-6 rounded-md shadow-lg bg-chart-2 text-primary hover:bg-chart-2/90 transition-all font-default font-bold">
                      <Link to="/#request-your-consultation">Book a consultation</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div id="our-procedures" className="mb-12 md:mb-24">
          <div className="text-center mb-12">
            <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-3">How We Treat Pain</h2>
            <p className="text-base text-muted-foreground font-default max-w-2xl mx-auto">
              Each procedure is selected based on your specific diagnosis. Select a treatment below to learn more.
            </p>
          </div>
          <div className="flex flex-col gap-3 max-w-3xl mx-auto">
            {procedures.map((proc, i) => <div key={i} className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden">
                <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left">
                  <span className="font-default font-bold text-primary text-base">{proc.title}</span>
                  <ChevronDown className={`w-5 h-5 text-[#65d19d] shrink-0 transition-transform duration-200 ${openIndex === i ? "rotate-180" : ""}`} />
                </button>
                {openIndex === i && <div className="px-6 pb-6 flex flex-col gap-4 border-t border-[#f1f3f5]">
                    <div className="pt-4">
                      <p className="font-default font-bold text-[#264256] text-sm mb-1">What is it?</p>
                      <p className="font-default text-muted-foreground text-sm leading-relaxed">{proc.what}</p>
                    </div>
                    <div>
                      <p className="font-default font-bold text-[#264256] text-sm mb-1">How is it performed?</p>
                      <p className="font-default text-muted-foreground text-sm leading-relaxed">{proc.how}</p>
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row-reverse items-stretch gap-6 md:gap-12 mb-12 md:mb-24">
          <div className="w-full lg:w-[45%] relative">
            <div className="relative w-full h-full min-h-[220px] md:min-h-[400px]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626927/img/Skausmo-spec.png" alt="Clinic Agatas consultation room" className="w-full h-full object-cover rounded-lg shadow-xl" src="https://charming-joey.10web.cloud/wp-content/uploads/2026/07/Agatas-iejimas-su-Irma-1.jpg" />
            </div>
          </div>
          <div className="w-full lg:w-[55%] flex flex-col gap-6">
            <div>
              <h2 className="font-default text-3xl md:text-4xl font-bold text-primary leading-tight mb-3">How it works</h2>
              <p className="text-base text-muted-foreground font-default">
                From your first message to your flight home, every step is coordinated by our team.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              {[{
              step: "1",
              title: "Online consultation",
              desc: "Speak with a pain specialist remotely. Share your history, scans, and symptoms. No GP referral needed."
            }, {
              step: "2",
              title: "Treatment plan",
              desc: "You receive a clear diagnosis, recommended procedures, and exact costs before you commit to anything."
            }, {
              step: "3",
              title: "Travel to Kaunas",
              desc: "Direct flights from Dublin take under 3 hours. We help coordinate your visit."
            }, {
              step: "4",
              title: "Procedure day",
              desc: "Minimally invasive, guided by ultrasound or X-ray where required. Most patients are mobile the same day."
            }, {
              step: "5",
              title: "After your visit",
              desc: "We provide all documentation needed for your CBD reimbursement application to the HSE."
            }].map(item => <div key={item.step} className="flex gap-4 items-start">
                  <div className="w-9 h-9 rounded-full bg-[#264256] text-white flex items-center justify-center font-bold font-default shrink-0 text-sm">
                    {item.step}
                  </div>
                  <div>
                    <p className="font-default font-bold text-primary text-base">{item.title}</p>
                    <p className="font-default text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="text-center mb-12">
            <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-3">Procedure Costs</h2>
            <p className="text-base text-muted-foreground font-default max-w-2xl mx-auto">Every treatment plan is individual, so your final cost is too. The procedures you need, and how many, are decided by our specialists after they assess your condition. The prices below are a guide to help you plan. We confirm your full, personal costs before you travel, and many procedures are eligible for HSE reimbursement under the EU Cross-Border Directive.</p>
          </div>
          <div className="flex flex-col lg:flex-row gap-6 md:gap-12 items-stretch">
            <div className="w-full lg:w-[55%] bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden">
              <div className="bg-[#264256] px-6 py-4">
                <h3 className="font-default font-bold text-white text-lg">Getting Started</h3>
              </div>
              <div className="divide-y divide-[#f1f3f5]">
                {[{
                name: "In-person / Online consultation",
                price: "€150"
              }, {
                name: "Repeat consultation",
                price: "€90"
              }].map((proc, i) => <div key={i} className="flex items-center justify-between px-6 py-4">
                    <span className="font-default text-[#264256] text-sm">{proc.name}</span>
                    <span className="font-default font-bold text-[#264256] text-sm shrink-0 ml-4">{proc.price}</span>
                  </div>)}
              </div>
              <div className="bg-[#264256] px-6 py-4">
                <h3 className="font-default font-bold text-white text-lg">Procedures We Perform</h3>
              </div>
              <div className="divide-y divide-[#f1f3f5]">
                {[{
                name: "Invasive procedure (TTB)",
                price: "from €115"
              }, {
                name: "Invasive procedure under ultrasound / X-ray control",
                price: "from €360"
              }, {
                name: "Epidural steroid injection",
                price: "€420"
              }, {
                name: "Cryodenervation",
                price: "€865"
              }, {
                name: "PRP therapy",
                price: "€865"
              }].map((proc, i) => <div key={i} className="flex items-center justify-between px-6 py-4">
                    <span className="font-default text-[#264256] text-sm">{proc.name}</span>
                    <span className="font-default font-bold text-[#264256] text-sm shrink-0 ml-4">{proc.price}</span>
                  </div>)}
              </div>
            </div>
            <div className="w-full lg:w-[45%] flex flex-col gap-6">
              <div className="relative w-full h-full min-h-[200px] md:min-h-[300px] rounded-lg overflow-hidden shadow-xl">
                <img src="https://charming-joey.10web.cloud/wp-content/uploads/2026/06/DSC06594-Daktares-Rentgenas-Portretas-AGATAS-V01-1920x1280-1.jpg" alt="Clinic Agatas pain specialist" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
                <p className="font-default text-[#264256] text-sm text-center">Reimbursement under the EU Cross-Border Directive is subject to individual  HSE assessment. We provide all required documentation to support your claim.</p>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="text-base h-14 px-10 rounded-md shadow-lg bg-[#264256] text-white hover:bg-[#264256]/90 transition-all font-default font-bold">
              <Link to="/#request-your-consultation">Book a consultation</Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="text-base h-14 px-10 rounded-md border-2 border-[#264256] text-[#264256] hover:bg-[#264256] hover:text-white transition-all font-default font-bold">
              <Link to="/our-specialists#pain-specialists">Our specialists</Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}